"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { Loader2, PenLine, Plus, Trash2 } from "lucide-react";
import { groupByKnowledge } from "@/lib/knowledge-grouping";
import { useAppShell } from "@/context/AppShellContext";
import {
  distinctKnowledge,
  filterItemsByKnowledge,
  selectKnowledgeGroups,
  selectKnowledgeScope,
} from "@/lib/knowledge-scope";
import {
  createTalkNoteDocument,
  deleteTalkNoteDocument,
  listTalkNoteDocuments,
  type TalkNoteDocumentSummary,
} from "@/lib/talk-note-api";
import { notifyTalkNoteChanged } from "@/lib/talk-note-events";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { KnowledgeScopeSelect } from "@/components/knowledge/KnowledgeScopeSelect";
import { normalizeLanguage } from "@/i18n/init";
import { templateContent, type TalkNoteTemplate } from "./templates";
import TemplatePickerModal from "./TemplatePickerModal";

function relativeTime(seconds: number): string {
  if (!seconds || Number.isNaN(seconds)) return "";
  const diff = Date.now() / 1000 - seconds;
  // Compact locale-neutral units; the card wraps this as "Updated {x} ago".
  if (diff < 60) return "1m";
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

export default function TalkNoteHomePage() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { activeKnowledge } = useAppShell();
  const [initialKnowledge] = useState(activeKnowledge);
  const [selectedKnowledge, setSelectedKnowledge] = useState<
    string | null | undefined
  >(undefined);
  const [documents, setDocuments] = useState<TalkNoteDocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  // Grouped view remains the default while the controls expose alternate paths.
  const [view, setView] = useState<"byKnowledge" | "all">("byKnowledge");

  const groups = useMemo(
    () => groupByKnowledge(documents, (doc) => doc.knowledge_bases),
    [documents],
  );
  const knowledgeOptions = useMemo(
    () =>
      distinctKnowledge(
        groups,
        (group) => (group.isUncategorized ? [] : [group.key]),
      ),
    [groups],
  );
  const scopeKnowledge = selectKnowledgeScope(
    knowledgeOptions,
    selectedKnowledge === undefined ? initialKnowledge : selectedKnowledge,
  );
  const scopedDocuments = useMemo(
    () =>
      filterItemsByKnowledge(
        documents,
        scopeKnowledge,
        (doc) => doc.knowledge_bases,
      ),
    [documents, scopeKnowledge],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const docs = await listTalkNoteDocuments();
      setDocuments(docs);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = useCallback(
    async (content: string) => {
      if (creating) return;
      setCreating(true);
      setError("");
      try {
        const document = await createTalkNoteDocument({ content });
        notifyTalkNoteChanged();
        router.push(`/talk-note/${document.id}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setCreating(false);
      }
    },
    [creating, router],
  );

  const handlePickTemplate = useCallback(
    (tpl: TalkNoteTemplate) => {
      const lang = normalizeLanguage(i18n.language);
      void handleCreate(templateContent(tpl, lang));
    },
    [handleCreate, i18n.language],
  );

  const handleDelete = useCallback(
    async (docId: string) => {
      if (deletingId) return;
      setDeletingId(docId);
      setError("");
      try {
        await deleteTalkNoteDocument(docId);
        setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
        notifyTalkNoteChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setPendingDeleteId(null);
        setDeletingId(null);
      }
    },
    [deletingId],
  );

  const renderDocumentCard = (
    doc: TalkNoteDocumentSummary,
    unlinked = false,
  ) => {
    const isDeleting = deletingId === doc.id;
    const openDocument = () => router.push(`/talk-note/${doc.id}`);
    const requestDelete = () => {
      setPendingDeleteId(doc.id);
    };
    return (
      <article
        key={doc.id}
        data-document-id={doc.id}
        aria-busy={isDeleting || undefined}
        className={
          unlinked ? "talk-note-unlinked-card" : "talk-note-card"
        }
      >
        <Link
          href={`/talk-note/${doc.id}`}
          className={
            unlinked
              ? "talk-note-unlinked-card__link"
              : "talk-note-card__link"
          }
          aria-keyshortcuts="Space Delete Backspace"
          onKeyDown={(event) => {
            if (event.key === " ") {
              event.preventDefault();
              openDocument();
              return;
            }
            if (event.key === "Delete" || event.key === "Backspace") {
              event.preventDefault();
              requestDelete();
            }
          }}
        >
          <div
            className={
              unlinked
                ? "talk-note-unlinked-card__content"
                : "talk-note-card__content"
            }
          >
            <h3
              className={
                unlinked
                  ? "talk-note-unlinked-card__title"
                  : "talk-note-card__title"
              }
              title={doc.title || t("Untitled draft")}
            >
              {doc.title || t("Untitled draft")}
            </h3>
            <p
              className={
                unlinked
                  ? "talk-note-unlinked-card__description"
                  : "talk-note-card__preview"
              }
            >
              {unlinked
                ? t("Not linked to any knowledge map")
                : doc.preview || t("Empty draft")}
            </p>
            {!unlinked ? (
              <time className="talk-note-card__time">
                {relativeTime(doc.updated_at)
                  ? t("Updated {{ago}} ago", {
                      ago: relativeTime(doc.updated_at),
                    })
                  : t("Updated")}
              </time>
            ) : null}
          </div>
        </Link>
        <div className="talk-note-card__actions">
          <button
            type="button"
            className="talk-note-card__delete"
            onClick={requestDelete}
            disabled={isDeleting}
            aria-label={t("Delete draft")}
            title={t("Delete draft")}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        </div>
      </article>
    );
  };

  const renderCards = (
    docs: readonly TalkNoteDocumentSummary[],
    unlinked = false,
  ) => (
    <div className="talk-note-grid">
      {docs.map((doc) => renderDocumentCard(doc, unlinked))}
    </div>
  );

  const renderEmpty = () => (
    <div className="talk-note-empty">
      <PenLine size={30} strokeWidth={1.5} className="talk-note-empty__icon" />
      <p className="talk-note-empty__title">{t("No drafts yet")}</p>
      <p className="talk-note-empty__description">
        {t("Start a new markdown draft to begin writing.")}
      </p>
      <div className="talk-note-empty__actions">
        <button
          type="button"
          onClick={() => handleCreate("")}
          disabled={creating}
          className="talk-note-empty__action talk-note-empty__action--primary"
        >
          {creating ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
          {t("New draft")}
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={creating}
          className="talk-note-empty__action"
        >
          {t("Start from template")}
        </button>
      </div>
    </div>
  );

  const linkedGroups = groups.filter((group) => !group.isUncategorized);
  const visibleLinkedGroups = selectKnowledgeGroups(
    linkedGroups,
    scopeKnowledge,
  );
  const unlinkedGroup =
    scopeKnowledge === null
      ? groups.find((group) => group.isUncategorized)
      : undefined;
  const hasNoScopedNotes =
    !error &&
    !loading &&
    scopeKnowledge !== null &&
    visibleLinkedGroups.length === 0;
  const listState = error
    ? "error"
    : loading
      ? "loading"
      : hasNoScopedNotes
        ? "filtered-empty"
        : documents.length === 0
          ? "empty"
          : "ready";

  return (
    <div className="talk-note-page" id="tn-root" data-state={listState}>
      <div className="talk-note-wrap">
        <header className="talk-note-header">
          <h2>
            <span>{t("TalkNote")}</span>
            <span className="talk-note-header__english">{t("Talk Notes")}</span>
          </h2>
          <p>{t("My notes grouped by knowledge map")}</p>
          {!loading && !error ? (
            <KnowledgeScopeSelect
              options={knowledgeOptions}
              value={scopeKnowledge}
              onChange={setSelectedKnowledge}
            />
          ) : null}
          <button
            type="button"
            onClick={() => handleCreate("")}
            disabled={creating}
            className="talk-note-new-button"
          >
            {creating ? <Loader2 size={15} className="spin" /> : <Plus size={15} />}
            {t("New note")}
          </button>
        </header>

        {!loading && !error && documents.length > 0 ? (
          <div className="talk-note-view-actions" aria-label={t("Document view")}>
            <button
              type="button"
              className="talk-note-view-actions__button"
              onClick={() => setView("byKnowledge")}
              aria-pressed={view === "byKnowledge"}
            >
              {t("By knowledge")}
            </button>
            <button
              type="button"
              className="talk-note-view-actions__button"
              onClick={() => setView("all")}
              aria-pressed={view === "all"}
            >
              {t("All")}
            </button>
            <button
              type="button"
              className="talk-note-view-actions__button"
              onClick={() => setPickerOpen(true)}
            >
              {t("From template")}
            </button>
          </div>
        ) : null}

        {error ? (
          <div className="talk-note-error" role="alert">
            <strong>{t("Failed to load drafts.")}</strong>
            <span>{error}</span>
          </div>
        ) : loading ? (
          <div className="talk-note-loading" aria-busy="true">
            <Loader2 size={16} className="spin" />
            {t("Loading drafts…")}
          </div>
        ) : hasNoScopedNotes ? (
          <div className="talk-note-empty">
            <PenLine
              size={30}
              strokeWidth={1.5}
              className="talk-note-empty__icon"
            />
            <p className="talk-note-empty__title">
              {t("No talk notes connected to this knowledge")}
            </p>
            <div className="talk-note-empty__actions">
              <button
                type="button"
                onClick={() => setSelectedKnowledge(null)}
                className="talk-note-empty__action"
              >
                {t("View All")}
              </button>
            </div>
          </div>
        ) : documents.length === 0 ? (
          renderEmpty()
        ) : view === "all" ? (
          renderCards(scopedDocuments)
        ) : (
          <div className="talk-note-groups">
            {visibleLinkedGroups.map((group) => (
              <section key={group.key} className="talk-note-group">
                <header className="talk-note-group__header">
                  <span className="talk-note-group__chip">{group.label}</span>
                  <span className="talk-note-group__count">
                    {t("{{count}} notes", { count: group.items.length })}
                  </span>
                </header>
                {renderCards(group.items)}
              </section>
            ))}
            {unlinkedGroup ? (
              <section className="talk-note-group talk-note-group--unlinked">
                <header className="talk-note-group__header">
                  <span className="talk-note-group__chip talk-note-group__chip--unlinked">
                    {t("No knowledge map")}
                  </span>
                  <span className="talk-note-group__unlinked-label">
                    {t("Unlinked")}
                  </span>
                  <p className="talk-note-group__unlinked-description">
                    {t("{{count}} notes · Awaiting organization proposals in Memory", {
                      count: unlinkedGroup.items.length,
                    })}
                  </p>
                  <button
                    type="button"
                    className="talk-note-memory-proposal"
                    onClick={() => router.push("/memory")}
                  >
                    {t("View organization proposals →")}
                  </button>
                </header>
                {renderCards(unlinkedGroup.items, true)}
              </section>
            ) : null}
          </div>
        )}
      </div>

      <TemplatePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickTemplate}
        creating={creating}
      />

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title={t("Delete draft")}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        tone="danger"
        busy={deletingId !== null}
        onConfirm={() => {
          if (pendingDeleteId) void handleDelete(pendingDeleteId);
        }}
        onCancel={() => setPendingDeleteId(null)}
      >
        {t('"{{name}}" will be permanently deleted.', {
          name:
            documents.find((doc) => doc.id === pendingDeleteId)?.title ||
            t("Untitled draft"),
        })}
      </ConfirmDialog>

      <style jsx global>{`
        #tn-root {
          height: 100%;
          overflow: auto;
          background: var(--background);
          color: var(--foreground);
        }
        #tn-root .talk-note-wrap {
          max-width: 1080px;
          margin: 0 auto;
          padding: 22px 28px 60px;
        }
        #tn-root .spin {
          animation: tn-sp 0.9s linear infinite;
        }
        @keyframes tn-sp {
          to {
            transform: rotate(360deg);
          }
        }

        #tn-root .talk-note-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          min-height: 40px;
          margin-bottom: 16px;
        }
        #tn-root .talk-note-header h2 {
          display: flex;
          align-items: baseline;
          flex: none;
          gap: 10px;
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          line-height: 27.9px;
          letter-spacing: -0.01em;
        }
        #tn-root .talk-note-header__english {
          color: var(--muted-foreground);
          font-weight: 600;
        }
        #tn-root .talk-note-header p {
          min-width: 0;
          margin: 0 auto 0 0;
          overflow: hidden;
          color: var(--muted-foreground);
          font-size: 13.5px;
          font-weight: 400;
          line-height: 19.375px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        #tn-root .talk-note-new-button,
        #tn-root .talk-note-empty__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          height: 40px;
          padding: 0 15px;
          border: 1px solid var(--primary);
          border-radius: 11px;
          background: var(--primary);
          color: var(--primary-foreground);
          font-size: 13.5px;
          font-weight: 700;
          white-space: nowrap;
          cursor: pointer;
          transition: opacity 0.15s var(--ease);
        }
        #tn-root .talk-note-new-button:hover,
        #tn-root .talk-note-empty__action--primary:hover {
          opacity: 0.9;
        }
        #tn-root .talk-note-new-button:focus-visible,
        #tn-root .talk-note-empty__action:focus-visible,
        #tn-root .talk-note-card__link:focus-visible,
        #tn-root .talk-note-unlinked-card__link:focus-visible,
        #tn-root .talk-note-card__delete:focus-visible,
        #tn-root .talk-note-view-actions__button:focus-visible,
        #tn-root .talk-note-memory-proposal:focus-visible {
          outline: 2px solid var(--ring);
          outline-offset: 2px;
        }
        #tn-root .talk-note-new-button:disabled,
        #tn-root .talk-note-empty__action:disabled {
          cursor: default;
          opacity: 0.55;
        }
        #tn-root .talk-note-view-actions {
          display: flex;
          justify-content: flex-end;
          gap: 4px;
          margin: -8px 0 14px;
        }
        #tn-root .talk-note-view-actions__button {
          min-height: 28px;
          padding: 0 8px;
          border: 1px solid transparent;
          border-radius: 7px;
          background: transparent;
          color: var(--muted-foreground);
          font-size: 13.5px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s var(--ease), color 0.15s var(--ease);
        }
        #tn-root .talk-note-view-actions__button:hover,
        #tn-root .talk-note-view-actions__button[aria-pressed="true"] {
          background: var(--secondary);
          color: var(--foreground);
        }

        #tn-root .talk-note-groups {
          display: flex;
          flex-direction: column;
          gap: 26px;
        }
        #tn-root .talk-note-group__header {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          min-height: 25.81px;
          gap: 8px;
          margin-bottom: 10px;
        }
        #tn-root .talk-note-group__chip {
          display: inline-flex;
          align-items: center;
          height: 25.81px;
          padding: 4px 10px;
          border-radius: 999px;
          background: var(--secondary);
          color: var(--foreground);
          font-size: 13.5px;
          font-weight: 700;
          line-height: 17.81px;
          white-space: nowrap;
        }
        #tn-root .talk-note-group__chip--unlinked {
          background: var(--muted);
        }
        #tn-root .talk-note-group__count,
        #tn-root .talk-note-group__unlinked-label,
        #tn-root .talk-note-group__unlinked-description {
          margin: 0;
          color: var(--muted-foreground);
          font-size: 13.5px;
          font-weight: 400;
          line-height: 18.6px;
        }
        #tn-root .talk-note-group__unlinked-label {
          font-weight: 700;
        }
        #tn-root .talk-note-memory-proposal {
          margin-left: auto;
          padding: 1px 6px;
          color: var(--primary);
          font-size: 13.5px;
          font-weight: 700;
          line-height: 19.375px;
          white-space: nowrap;
          cursor: pointer;
        }
        #tn-root .talk-note-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(246px, 1fr));
          gap: 12px;
        }
        #tn-root .talk-note-card,
        #tn-root .talk-note-unlinked-card {
          position: relative;
          min-width: 0;
          text-align: left;
        }
        #tn-root .talk-note-card {
          border: 1px solid var(--border);
          border-radius: 14px;
          background: var(--card);
          box-shadow: var(--shadow-soft);
          transition: border-color 0.15s var(--ease), transform 0.15s var(--ease);
        }
        #tn-root .talk-note-card:hover {
          border-color: var(--ring);
          transform: translateY(-1px);
        }
        #tn-root .talk-note-card__link,
        #tn-root .talk-note-unlinked-card__link {
          display: flex;
          min-width: 0;
          min-height: 100%;
          padding: 14px 48px 14px 15px;
          color: inherit;
          text-align: left;
          text-decoration: none;
          cursor: pointer;
        }
        #tn-root .talk-note-card__content {
          display: flex;
          min-width: 0;
          flex: 1;
          flex-direction: column;
          gap: 7px;
        }
        #tn-root .talk-note-card__title,
        #tn-root .talk-note-unlinked-card__title {
          margin: 0;
          overflow: hidden;
          font-size: 14.5px;
          font-weight: 700;
          line-height: 22.475px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        #tn-root .talk-note-card__preview {
          display: -webkit-box;
          margin: 0;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          color: var(--muted-foreground);
          font-size: 13.5px;
          font-weight: 400;
          line-height: 19.375px;
        }
        #tn-root .talk-note-card__time {
          margin-top: auto;
          color: var(--muted-foreground);
          font-size: 13px;
          font-weight: 400;
          line-height: 17.05px;
        }
        #tn-root .talk-note-unlinked-card {
          border: 1px dashed var(--border);
          border-radius: 14px;
          background: transparent;
        }
        #tn-root .talk-note-unlinked-card:hover {
          border-color: var(--ring);
        }
        #tn-root .talk-note-unlinked-card__link {
          min-height: 75.28px;
        }
        #tn-root .talk-note-unlinked-card__content {
          min-width: 0;
          flex: 1;
        }
        #tn-root .talk-note-unlinked-card__description {
          margin: 0;
          overflow: hidden;
          color: var(--muted-foreground);
          font-size: 13.5px;
          font-weight: 400;
          line-height: 19.375px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        #tn-root .talk-note-card__actions {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          opacity: 0;
          pointer-events: none;
          transform: translateY(-2px);
          transition: opacity 0.15s var(--ease), transform 0.15s var(--ease);
        }
        #tn-root .talk-note-card:hover .talk-note-card__actions,
        #tn-root .talk-note-card:focus-within .talk-note-card__actions,
        #tn-root .talk-note-unlinked-card:hover .talk-note-card__actions,
        #tn-root .talk-note-unlinked-card:focus-within .talk-note-card__actions {
          opacity: 1;
          pointer-events: auto;
          transform: translateY(0);
        }
        #tn-root .talk-note-card__delete {
          display: inline-flex;
          width: 28px;
          height: 28px;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--card);
          color: var(--muted-foreground);
          cursor: pointer;
          transition: border-color 0.15s var(--ease), color 0.15s var(--ease);
        }
        #tn-root .talk-note-card__delete:hover {
          border-color: var(--destructive);
          color: var(--destructive);
        }
        #tn-root .talk-note-card__delete:disabled {
          cursor: default;
          opacity: 0.55;
        }

        #tn-root .talk-note-empty {
          display: flex;
          min-height: 360px;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0 32px;
          border: 1px dashed var(--border);
          border-radius: 16px;
          text-align: center;
        }
        #tn-root .talk-note-empty__icon {
          margin-bottom: 12px;
          color: var(--muted-foreground);
        }
        #tn-root .talk-note-empty__title {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
        }
        #tn-root .talk-note-empty__description {
          max-width: 380px;
          margin: 6px 0 0;
          color: var(--muted-foreground);
          font-size: 13.5px;
          line-height: 1.5;
        }
        #tn-root .talk-note-empty__actions {
          display: flex;
          gap: 10px;
          margin-top: 18px;
        }
        #tn-root .talk-note-empty__action:not(.talk-note-empty__action--primary) {
          border-color: var(--border);
          background: var(--card);
          color: var(--foreground);
        }

        #tn-root .talk-note-error {
          display: grid;
          gap: 4px;
          padding: 10px 13px;
          border: 1px solid color-mix(in srgb, var(--destructive) 30%, transparent);
          border-radius: 10px;
          background: color-mix(in srgb, var(--destructive) 8%, var(--card));
          color: var(--destructive);
          font-size: 13.5px;
          line-height: 1.5;
        }
        #tn-root .talk-note-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 80px 0;
          color: var(--muted-foreground);
          font-size: 13.5px;
        }

        @media (max-width: 720px) {
          #tn-root .talk-note-wrap {
            padding: 18px 18px 56px;
          }
          #tn-root .talk-note-header {
            align-items: flex-start;
          }
          #tn-root .talk-note-header h2 {
            flex-wrap: wrap;
          }
          #tn-root .talk-note-group__unlinked-description {
            flex-basis: 100%;
          }
          #tn-root .talk-note-memory-proposal {
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
}
